import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TIMEZONES } from "@/lib/timezones";
import { DatalistInput } from "./DatalistInput";
import { Shield, UserCog, User } from "lucide-react";

const ROLES = [
  { value: "employee", label: "Employee", desc: "Regular staff", icon: User, color: "border-slate-500 text-slate-200 bg-slate-500/10", active: "bg-slate-600/40 border-slate-400 text-white" },
  { value: "manager", label: "Manager", desc: "Team lead", icon: UserCog, color: "border-[#00e5ff]/40 text-[#00e5ff] bg-[#00e5ff]/8", active: "bg-[#00e5ff]/20 border-[#00e5ff] text-white" },
  { value: "admin", label: "Admin", desc: "Full access", icon: Shield, color: "border-[color:var(--accent)]/40 text-[color:var(--accent)] bg-[color:var(--accent)]/8", active: "bg-[color:var(--accent)]/25 border-[color:var(--accent)] text-white" },
];

export function NewUserModal({ users, taxonomies, onClose, onCreated }) {
  const [f, setF] = useState({
    email: "", password: "", first_name: "", discord_name: "", role: "employee",
    department: "", job: "", title: "", location: "", timezone: "", start_date: "", birthdate: "", manager_id: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, manager_id: f.manager_id || null };
    try {
      await api.post("/auth/register", payload);
      toast.success("User created");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const inputCls = "bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in" onClick={onClose}>
      <form
        onSubmit={submit}
        className="card-surface p-6 w-full max-w-lg fade-in-up max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-user-modal"
      >
        <h2 className="text-xl font-bold mb-1">Add employee directly</h2>
        <p className="text-xs text-slate-500 mb-4">Skips email — you'll share the temporary password yourself.</p>

        {/* PROMINENT ROLE PICKER */}
        <div className="mb-5 pb-4 border-b border-slate-800">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold mb-2">Role in the company</div>
          <div className="grid grid-cols-3 gap-2" data-testid="nu-role-picker">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = f.role === r.value;
              return (
                <button
                  type="button"
                  key={r.value}
                  onClick={() => set("role", r.value)}
                  data-testid={`nu-role-${r.value}`}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border-2 transition-all ${active ? r.active : `${r.color} hover:border-slate-400`}`}
                >
                  <Icon size={18} />
                  <div className="text-sm font-bold">{r.label}</div>
                  <div className="text-[10px] opacity-80 leading-none">{r.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="First name" required data-testid="nu-first-name" className={inputCls} />
          <input value={f.discord_name} onChange={(e) => set("discord_name", e.target.value)} placeholder="Discord name" data-testid="nu-discord-name" className={inputCls} />
          <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" type="email" required data-testid="nu-email" className={inputCls} />
          <input value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="Temporary password" required data-testid="nu-password" className={inputCls} />
          <input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" data-testid="nu-title" className={inputCls} />
          <DatalistInput value={f.department} onChange={(v) => set("department", v)} options={taxonomies.department} placeholder="Department" testid="nu-dept" className={inputCls} />
          <DatalistInput value={f.job} onChange={(v) => set("job", v)} options={taxonomies.job || []} placeholder="Job" testid="nu-job" className={inputCls} />
          <DatalistInput value={f.location} onChange={(v) => set("location", v)} options={taxonomies.location} placeholder="Location" testid="nu-location" className={inputCls} />
          <select value={f.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="nu-timezone" className={inputCls}>
            <option value="">Timezone (optional)</option>
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
          <select value={f.manager_id} onChange={(e) => set("manager_id", e.target.value)} data-testid="nu-manager" className={inputCls}>
            <option value="">No manager</option>
            {users.filter((u) => u.role !== "employee").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <label className="flex flex-col text-xs text-slate-500">Start date
            <input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="nu-start-date" className={`${inputCls} mt-1`} />
          </label>
          <label className="flex flex-col text-xs text-slate-500">Birthday
            <input type="date" value={f.birthdate} onChange={(e) => set("birthdate", e.target.value)} data-testid="nu-birthdate" className={`${inputCls} mt-1`} />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors">Cancel</button>
          <button type="submit" data-testid="nu-submit" className="px-4 py-2 rounded-lg bg-[color:var(--accent)] text-white font-bold text-sm">Create {ROLES.find((r) => r.value === f.role)?.label}</button>
        </div>
      </form>
    </div>
  );
}

export default NewUserModal;
